import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { ReactNode, useRef, useState } from 'react';
import { CustomDropdown } from 'src/components';

/**
 * Props for the DropdownStyled component
 * @param {string} className - CSS classes for the dropdown button
 * @param {ReactNode} buttonContent - Content to display in the dropdown button
 * @param {ReactNode | ((closeDropdown: () => void) => ReactNode)} children - The dropdown content or a render function that receives the close function
 * @param {object} customStyle - Optional custom styles for the dropdown
 */
type Props = {
  className?: string;
  buttonContent: ReactNode;
  children: ReactNode | ((closeDropdown: () => void) => ReactNode);
  customStyle?: Record<string, string>;
  customHeight?: string;
  darkStyle?: boolean;
  enforceMaxHeight?: boolean;
  enableScrollY?: boolean;
  disabled?: boolean;
};

/**
 * Reusable styled dropdown component that maintains consistent behavior
 * while allowing flexible styling and content
 */
export const DropdownStyled = ({
  className = '',
  buttonContent,
  children,
  customStyle = {
    marginTop: '6px !important',
    marginRight: '-2px !important',
  },
  customHeight,
  darkStyle = true,
  enforceMaxHeight = false,
  enableScrollY = false,
  disabled = false,
}: Props) => {
  const [open, setOpen] = useState<boolean>(false);
  const anchorEl = useRef<HTMLDivElement>(null);

  const handleClick = () => {
    // Disabled blocks opening, but an already-open dropdown must still close
    // (e.g. it can become disabled while open via the click-away path).
    if (disabled && !open) return;
    setOpen((previousOpen) => !previousOpen);
  };

  const closeDropdown = () => {
    setOpen(false);
  };

  const canBeOpen = open && Boolean(anchorEl);
  const id = canBeOpen ? 'transition-popper' : undefined;

  return (
    <div>
      <div
        className={`flex items-center justify-between rounded px-3 py-1 font-semibold transition-all ${
          disabled ? 'cursor-default' : 'cursor-pointer'
        } ${customHeight ? customHeight : 'h-12'} ${
          open ? 'ring-2 ring-primary' : ''
        } ${
          darkStyle
            ? 'bg-primary-off hover:bg-primary-offHover'
            : disabled
              ? 'bg-cloudGray'
              : 'bg-cloudGray hover:bg-cloudGray/80'
        } ${className}`}
        aria-describedby={id}
        onClick={handleClick}
        ref={anchorEl}
      >
        {buttonContent}
        <KeyboardArrowUpIcon
          style={{
            fontSize: '28px',
            transform: open ? 'rotate(0deg)' : 'rotate(180deg)',
            transition: 'transform 150ms cubic-bezier(0.4, 0, 0.2, 1)',
          }}
          className={`-mr-[7px] text-blueGray ${disabled ? 'cursor-default' : 'cursor-pointer'}`}
        />
      </div>
      <CustomDropdown
        id={id}
        open={open}
        anchorEl={anchorEl.current}
        handleClick={handleClick}
        customStyle={customStyle}
        enforceMaxHeight={enforceMaxHeight}
        enableScrollY={enableScrollY}
      >
        {typeof children === 'function' ? children(closeDropdown) : children}
      </CustomDropdown>
    </div>
  );
};
